/**
 * Identifies old notification cards that belong to work the signed-in user
 * has finished. Source authority fields win over links. Links are only a
 * compatibility fallback for older task and message cards that predate those
 * fields, and only root-relative Hub links are accepted.
 */

import { legacyAwardCardTaskIds } from './studentAwardTask.ts';

export interface CompletionNotificationRef {
  /** Whose card it is. Read only by the legacy award fold in notificationMatchesTask. */
  uid?: unknown;
  kind?: unknown;
  threadId?: unknown;
  communityThreadId?: unknown;
  taskId?: unknown;
  classPostId?: unknown;
  /** Backing private Intern/executive-board discussion (board.ts). */
  discussionId?: unknown;
  compBoardPostId?: unknown;
  /** Backing parent coordination room and message (bagGroups.ts). */
  bagGroupId?: unknown;
  bagMessageId?: unknown;
  /** Backing sub request (subs.ts). */
  subRequestId?: unknown;
  /** Backing class whose production fact changed (notify.mjs's production lane). */
  productionOfferingId?: unknown;
  /** Backing studio announcement, stamped by notificationAuthority.mjs's
   *  unscoped lane from 2026-09-25 on, with the Show again round it was sent
   *  in. Older announcement cards carry neither; see notificationMatchesBulletin. */
  bulletinId?: unknown;
  resendEpoch?: unknown;
  link?: unknown;
  /**
   * When this exact card was minted, which is the moment the message behind it
   * arrived. Source authority in its own right: it is how a filing action says
   * "through THIS message" instead of "through whatever is in there now". See
   * notificationCardVersionMs below.
   */
  createdAt?: unknown;
}

export interface NotificationCaptureRef extends CompletionNotificationRef {
  uid?: unknown;
  senderUid?: unknown;
  kind?: unknown;
  title?: unknown;
  body?: unknown;
}

export interface CapturedNotificationIdentity {
  id: string;
  uid: string | null;
  senderUid: string | null;
  threadId: string | null;
  communityThreadId: string | null;
  classPostId: string | null;
  discussionId: string | null;
  compBoardPostId: string | null;
  bagGroupId: string | null;
  bagMessageId: string | null;
  subRequestId: string | null;
  link: string | null;
  kind: string | null;
  title: string | null;
  body: string | null;
  createdAtMs: number | null;
}

export type NotificationInboxSource = {
  kind: 'thread' | 'classPost';
  itemId: string;
};

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function decodeId(value: string): string | null {
  try {
    const decoded = decodeURIComponent(value);
    return validDecodedId(decoded);
  } catch {
    return null;
  }
}

function validDecodedId(value: string): string | null {
  return value.length > 0 && !value.includes('/') && !/[\u0000-\u001f\u007f]/u.test(value)
    ? value
    : null;
}

/** Parse only an app-owned, root-relative link. */
function internalUrl(link: unknown): URL | null {
  if (typeof link !== 'string' || link.length === 0 || link !== link.trim()) return null;
  if (!link.startsWith('/') || link.startsWith('//')) return null;
  if (link.includes('\\') || /[\u0000-\u001f\u007f]/u.test(link)) return null;
  try {
    const url = new URL(link, 'https://hub.invalid');
    return url.origin === 'https://hub.invalid' ? url : null;
  } catch {
    return null;
  }
}

function threadIdFromInternalLink(link: unknown): string | null {
  const url = internalUrl(link);
  if (!url) return null;
  const match = /^\/messages\/([^/]+)$/u.exec(url.pathname);
  return match ? decodeId(match[1]) : null;
}

function taskIdFromInternalLink(link: unknown): string | null {
  const url = internalUrl(link);
  if (!url) return null;

  const candidates: string[] = [];
  const pathMatch = /^\/tasks\/([^/]+)$/u.exec(url.pathname);
  if (pathMatch) {
    const decoded = decodeId(pathMatch[1]);
    if (!decoded) return null;
    candidates.push(decoded);
  } else if (url.pathname !== '/tasks') {
    return null;
  }

  const hash = url.hash.startsWith('#task-') ? url.hash.slice('#task-'.length) : '';
  if (hash) {
    const decoded = decodeId(hash);
    if (!decoded) return null;
    candidates.push(decoded);
  }

  for (const key of ['task', 'taskId']) {
    const value = url.searchParams.get(key);
    if (!value) continue;
    const decoded = validDecodedId(value);
    if (!decoded) return null;
    candidates.push(decoded);
  }

  const unique = [...new Set(candidates)];
  return unique.length === 1 ? unique[0] : null;
}

function classPostIdFromInternalLink(link: unknown): string | null {
  const url = internalUrl(link);
  if (!url || !/^\/class\/[^/]+(?:\/feed)?$/u.test(url.pathname)) return null;
  const prefix = '#class-post-';
  if (!url.hash.startsWith(prefix)) return null;
  return decodeId(url.hash.slice(prefix.length));
}

function timestampMillis(value: unknown): number | null {
  if (!value || typeof value !== 'object') return null;
  const toMillis = (value as { toMillis?: unknown }).toMillis;
  if (typeof toMillis !== 'function') return null;
  const millis = toMillis.call(value);
  return typeof millis === 'number' && Number.isFinite(millis) ? Math.floor(millis) : null;
}

/** Freeze the exact notification card that an inbox action observed. */
export function captureNotificationIdentity(
  id: string,
  notification: NotificationCaptureRef
): CapturedNotificationIdentity {
  return {
    id,
    uid: nonEmptyString(notification.uid),
    senderUid: nonEmptyString(notification.senderUid),
    threadId: nonEmptyString(notification.threadId),
    communityThreadId: nonEmptyString(notification.communityThreadId),
    classPostId: nonEmptyString(notification.classPostId),
    discussionId: nonEmptyString(notification.discussionId),
    compBoardPostId: nonEmptyString(notification.compBoardPostId),
    bagGroupId: nonEmptyString(notification.bagGroupId),
    bagMessageId: nonEmptyString(notification.bagMessageId),
    subRequestId: nonEmptyString(notification.subRequestId),
    link: nonEmptyString(notification.link),
    kind: nonEmptyString(notification.kind),
    title: typeof notification.title === 'string' ? notification.title : null,
    body: typeof notification.body === 'string' ? notification.body : null,
    createdAtMs: timestampMillis(notification.createdAt),
  };
}

export function notificationStillMatchesCapture(
  captured: CapturedNotificationIdentity,
  notification: NotificationCaptureRef
): boolean {
  const current = captureNotificationIdentity(captured.id, notification);
  return current.uid === captured.uid
    && current.senderUid === captured.senderUid
    && current.threadId === captured.threadId
    && current.communityThreadId === captured.communityThreadId
    && current.classPostId === captured.classPostId
    && current.discussionId === captured.discussionId
    && current.compBoardPostId === captured.compBoardPostId
    && current.bagGroupId === captured.bagGroupId
    && current.bagMessageId === captured.bagMessageId
    && current.subRequestId === captured.subRequestId
    && current.link === captured.link
    && current.kind === captured.kind
    && current.title === captured.title
    && current.body === captured.body
    && current.createdAtMs === captured.createdAtMs;
}

/**
 * Resolve the one message-inbox item behind a notification. Canonical source
 * fields always win. A legacy Hub link is considered only when no canonical
 * message field exists, and conflicting authority fails closed.
 */
export function notificationInboxSource(
  notification: CompletionNotificationRef
): NotificationInboxSource | null {
  const threadIds = [
    nonEmptyString(notification.threadId),
    nonEmptyString(notification.communityThreadId),
  ].filter((value): value is string => value !== null);
  const uniqueThreadIds = [...new Set(threadIds)];
  const classPostId = nonEmptyString(notification.classPostId);
  const hasCanonicalMessageSource = uniqueThreadIds.length > 0 || classPostId !== null;

  if (hasCanonicalMessageSource) {
    if (uniqueThreadIds.length === 1 && classPostId === null) {
      return { kind: 'thread', itemId: uniqueThreadIds[0] };
    }
    if (uniqueThreadIds.length === 0 && classPostId !== null) {
      return { kind: 'classPost', itemId: classPostId };
    }
    return null;
  }

  const legacyThreadId = threadIdFromInternalLink(notification.link);
  if (legacyThreadId) return { kind: 'thread', itemId: legacyThreadId };
  const legacyClassPostId = classPostIdFromInternalLink(notification.link);
  return legacyClassPostId ? { kind: 'classPost', itemId: legacyClassPostId } : null;
}

/**
 * THE MOMENT THIS CARD IS ABOUT, in milliseconds, or null when the card cannot
 * say. A card is minted for one message, so its own createdAt is the newest
 * thing behind it that the person has been shown. Every inbox action driven by
 * a card is bounded by this, which is what stops "I read the alert" from also
 * meaning "I read the reply that landed afterwards".
 *
 * Null rather than a guess when the stamp has not resolved yet (a serverTimestamp
 * still in flight reads as a FieldValue, not a Timestamp). A caller with no
 * moment must move no cursor: filing through a moment nobody can name is the
 * exact mistake this function exists to prevent.
 */
export function notificationCardVersionMs(
  notification: CompletionNotificationRef
): number | null {
  return timestampMillis(notification.createdAt);
}

/**
 * Resolve the class that owns one exact class-post notification without a
 * newest-N class-post query. Canonical authority and the URL anchor must name
 * the same post, so a conflicting legacy link fails closed.
 */
export function notificationClassPostOfferingId(
  notification: CompletionNotificationRef
): string | null {
  const source = notificationInboxSource(notification);
  if (source?.kind !== 'classPost') return null;
  const url = internalUrl(notification.link);
  if (!url) return null;
  const match = /^\/class\/([^/]+)(?:\/feed)?$/u.exec(url.pathname);
  if (!match || !url.hash.startsWith('#class-post-')) return null;
  const offeringId = decodeId(match[1]);
  const anchoredPostId = decodeId(url.hash.slice('#class-post-'.length));
  return offeringId && anchoredPostId === source.itemId ? offeringId : null;
}

/**
 * Match an ordinary or community message notification to one thread. When a
 * canonical authority field exists, a conflicting legacy link cannot make a
 * different thread's card match.
 */
export function notificationMatchesThread(
  notification: CompletionNotificationRef,
  threadId: string
): boolean {
  if (!threadId) return false;
  const authorityIds = [
    nonEmptyString(notification.threadId),
    nonEmptyString(notification.communityThreadId),
  ].filter((value): value is string => value !== null);
  const uniqueAuthorityIds = [...new Set(authorityIds)];
  if (uniqueAuthorityIds.length > 0) {
    return uniqueAuthorityIds.length === 1 && uniqueAuthorityIds[0] === threadId;
  }
  return threadIdFromInternalLink(notification.link) === threadId;
}

/**
 * Match a task notification by its canonical taskId when present, otherwise
 * by the old /tasks link shapes already used by reminders in this app, and
 * last by the legacy award card shape.
 *
 * THE LEGACY AWARD CARD (DECISIONS 170). Bobbie, 2026-10-27: "I she clicked
 * done with this several times yet it still shows as unread". The morning
 * reminder's card and the send-back's card were written with kind 'task', a
 * link to /student-awards?period=P and NO taskId, so Messages could not fold
 * them behind the to-do row and drew two rows about one thing. New cards
 * carry taskId. A card already on staging is bound the only safe way left:
 * its own uid plus the period in its link name exactly the two to-dos that
 * uid could hold for that round (lib/studentAwardTask.ts
 * legacyAwardCardTaskIds). Never a prefix match: a Director's card must not
 * fold behind a teacher's row.
 */
export function notificationMatchesTask(
  notification: CompletionNotificationRef,
  taskId: string
): boolean {
  if (!taskId) return false;
  const authorityId = nonEmptyString(notification.taskId);
  if (authorityId) return authorityId === taskId;
  const linked = taskIdFromInternalLink(notification.link);
  if (linked) return linked === taskId;
  return legacyAwardCardTaskId(notification, taskId);
}

const AWARD_PERIOD_KEY = /^\d{4}-\d{2}$/u;

/** The legacy award card shape, bound to the card's own uid. */
function legacyAwardCardTaskId(notification: CompletionNotificationRef, taskId: string): boolean {
  if (notification.kind !== 'task') return false;
  const uid = nonEmptyString(notification.uid);
  if (!uid) return false;
  const url = internalUrl(notification.link);
  if (!url || url.pathname !== '/student-awards') return false;
  const period = url.searchParams.get('period') ?? '';
  if (!AWARD_PERIOD_KEY.test(period)) return false;
  return legacyAwardCardTaskIds(period, uid).includes(taskId);
}

/** Match one class-post card by canonical source, then exact legacy anchor. */
export function notificationMatchesClassPost(
  notification: CompletionNotificationRef,
  classPostId: string
): boolean {
  if (!classPostId) return false;
  const authorityId = nonEmptyString(notification.classPostId);
  if (authorityId) return authorityId === classPostId;
  return classPostIdFromInternalLink(notification.link) === classPostId;
}

/**
 * Match one Intern/executive-board discussion's "New activity" card by its
 * canonical discussionId (board.ts, notify.mjs's discussion lane). Every
 * reply mints its own distinct notification doc for the same discussionId
 * (one card per reply, by design - see notify.mjs), so this matches ALL of
 * them at once: acknowledging or opening the discussion clears every card
 * it ever generated for the signed-in viewer, not just the newest one.
 */
export function notificationMatchesDiscussion(
  notification: CompletionNotificationRef,
  discussionId: string
): boolean {
  if (!discussionId) return false;
  return nonEmptyString(notification.discussionId) === discussionId;
}

/** A comp post's existing cards, including reply activity. No link guessing. */
export function notificationMatchesCompBoardPost(notification: CompletionNotificationRef, postId: string): boolean {
  return Boolean(postId) && nonEmptyString(notification.compBoardPostId) === postId;
}

/**
 * The one Messages row a comp board card was drawn in, as the sweep behind its
 * Done needs it (refute round 2 on the 2026-09-24 fix). Every card she holds
 * for one post, in one state, is one row (lib/compBoardNotifGroups.ts).
 */
export interface CompBoardRowBound {
  postId: string;
  /** The tapped row is a filed Past row, rather than an open one in Main. */
  filed: boolean;
  /** The newest createdAt among the cards the row showed when drawn, or null. */
  throughMs: number | null;
  /** The ids the row showed when drawn. */
  drawnIds: ReadonlySet<string>;
}

/**
 * Is this stored card part of the row she just tapped Done (or opened) on?
 *
 * BOUNDED BY STATE AND TIME, NOT BY WHICH CARDS WERE ON SCREEN. Messages
 * loads only the newest 40 undismissed cards, so a row drawn from that window
 * can stand for fewer cards than she holds: 45 replies to one post drew a
 * "40 new replies" row, Done cleared those 40, and the 5 older ones slid into
 * the freed slots as an identical row after a reload. So the row covers every
 * card for the post in the SAME state (open row: not dismissed; filed Past
 * row: dismissed ones only, never an open card in Main) minted at or before
 * the newest card the row showed. A reply minted after that is news she has
 * not seen and stays. A drawn card always counts, even if its stamp has not
 * resolved yet, as long as it is still in the row's state.
 */
export function notificationInCompBoardRow(
  notification: CompletionNotificationRef & { dismissed?: unknown },
  id: string,
  row: CompBoardRowBound
): boolean {
  if (!notificationMatchesCompBoardPost(notification, row.postId)) return false;
  if ((notification.dismissed === true) !== row.filed) return false;
  if (row.drawnIds.has(id)) return true;
  const cardMs = timestampMillis(notification.createdAt);
  return cardMs !== null && row.throughMs !== null && cardMs <= row.throughMs;
}

/** The newest resolved createdAt among the cards a row was drawn from. */
export function compBoardRowThroughMs(cards: readonly CompletionNotificationRef[]): number | null {
  let newest: number | null = null;
  for (const card of cards) {
    const ms = timestampMillis(card.createdAt);
    if (ms !== null && (newest === null || ms > newest)) newest = ms;
  }
  return newest;
}

/**
 * Match one parent coordination room's cards by its canonical bagGroupId
 * (bagGroups.ts, notify.mjs's 'bag' lane). Every message in the group mints
 * its own notification row with the same bagGroupId, so leaving
 * bagMessageId off matches every card the group ever generated at once,
 * mirroring notificationMatchesDiscussion. Pass bagMessageId to narrow the
 * match to one exact message's card instead.
 */
export function notificationMatchesBagGroup(
  notification: CompletionNotificationRef,
  bagGroupId: string,
  bagMessageId?: string
): boolean {
  if (!bagGroupId) return false;
  if (nonEmptyString(notification.bagGroupId) !== bagGroupId) return false;
  if (bagMessageId) return nonEmptyString(notification.bagMessageId) === bagMessageId;
  return true;
}

/**
 * Match the Big/Little alert cards that opening ONE group's board deals with
 * (DECISIONS 130): the alerts for that group's new posts, whose link is
 * /big-little?group=<group id> (lib/bigLittleAlerts.ts bigLittleGroupLink,
 * the server's compPairAlertLink), and the Monday question card, whose link
 * is /big-little. Another group's alerts wait for that group. Matched by the
 * link because these cards carry no other source field.
 */
export function notificationMatchesBigLittleGroup(
  notification: CompletionNotificationRef,
  groupId: string
): boolean {
  const link = nonEmptyString(notification.link);
  return link === '/big-little'
    || (groupId.length > 0 && link === `/big-little?group=${encodeURIComponent(groupId)}`);
}

/**
 * Match one sub request's cards by its canonical subRequestId (subs.ts,
 * notify.mjs's 'sub' lane).
 */
export function notificationMatchesSubRequest(
  notification: CompletionNotificationRef,
  subRequestId: string
): boolean {
  if (!subRequestId) return false;
  return nonEmptyString(notification.subRequestId) === subRequestId;
}

/** The announcement fields notificationMatchesBulletin needs. */
export interface BulletinCardRef {
  id: string;
  /** The announcement's stored title, exactly as it was sent. */
  title: string;
  /** When the announcement was created, in ms (0 when not known yet). */
  createdAtMs: number;
  /** The announcement's current Show again round (resendEpoch, 0 when unset). */
  resendEpoch: number;
}

/**
 * Match a studio announcement's bell card.
 *
 * Live report, 2026-09-24: acknowledging or dismissing an announcement never
 * cleared its card, because the card carried nothing that named the
 * announcement: kind 'bulletin', a title, a body, a link to /bulletins, and a
 * random document id. So the home bell dot stayed lit and /notifications kept
 * every announcement marked New. notificationAuthority.mjs now stamps
 * bulletinId and resendEpoch on every announcement card, and a stamped card
 * matches on those alone. A card from a LATER Show again round than the one
 * passed in is not matched: that round is news she has not acted on.
 *
 * Cards written before the stamp shipped are matched the only way left: kind
 * 'bulletin', the exact stored title, and minted no earlier than the
 * announcement itself. That is deliberately narrow. Two announcements with an
 * identical title can still both match a legacy card, which is the price of
 * clearing cards that name nothing; a stamped card never has that problem.
 */
export function notificationMatchesBulletin(
  notification: CompletionNotificationRef & { kind?: unknown; title?: unknown },
  bulletin: BulletinCardRef
): boolean {
  if (!bulletin.id) return false;
  const stampedId = nonEmptyString(notification.bulletinId);
  if (stampedId) {
    if (stampedId !== bulletin.id) return false;
    const cardEpoch = typeof notification.resendEpoch === 'number'
      && Number.isSafeInteger(notification.resendEpoch)
      && notification.resendEpoch >= 0
      ? notification.resendEpoch
      : 0;
    return cardEpoch <= bulletin.resendEpoch;
  }
  if (notification.kind !== 'bulletin') return false;
  if (typeof notification.title !== 'string' || notification.title !== bulletin.title) return false;
  const cardMs = timestampMillis(notification.createdAt);
  return cardMs !== null && bulletin.createdAtMs > 0 && cardMs >= bulletin.createdAtMs;
}

/**
 * Match every production-update card that says THE SAME THING about the same
 * class.
 *
 * BOBBIE, live, 2026-09-04: "I have marked the song name as done with this
 * several times but it does not go away." She was not wrong and nothing was
 * failing to write. notificationAuthority.mjs hashes the SONG NAME into the
 * document id (productionUpdateNotificationId), so correcting a typo or
 * swapping a track mints a brand new card - while the title it renders,
 * `Song name for ${className}`, is IDENTICAL for every one of them. She filed
 * one and an indistinguishable one was still sitting there. Several times.
 *
 * Matching on title as well as class is deliberate, and it is the
 * conservative direction. A class also mints "Final cut music for ..." cards
 * on the same productionOfferingId; those are a DIFFERENT fact she has not
 * seen yet, and filing a song-name card must never silently clear one. Under-
 * clearing costs a second tap. Over-clearing loses something she needed.
 *
 * A rename between two cards for one class leaves them ungrouped, which fails
 * in that same safe direction.
 *
 * Same shape as notificationMatchesDiscussion, which exists because of the
 * same complaint about board posts ("I have acknowledged this twice in fact
 * and it keeps showing up!").
 */
export function notificationMatchesProductionUpdate(
  notification: CompletionNotificationRef & { title?: unknown },
  productionOfferingId: string,
  title: string
): boolean {
  if (!productionOfferingId) return false;
  if (nonEmptyString(notification.productionOfferingId) !== productionOfferingId) return false;
  return typeof notification.title === 'string' && notification.title === title;
}
